import React, { useCallback, useRef, useState } from 'react';
import classNames from 'classnames/bind';
import PropTypes from 'prop-types';
import { useOnClickOutside } from '@beef/ui-kit/dist/hooks/useOnClickOutSide';

// Utils & Constants
import { getCurrentDay } from 'utils/dates';
import { ANALYTICS_DETAIL } from 'constants/detalization';
import { pushDetalizationAnalytics } from 'utils/analytics/detalization';

// Components
import { Text } from '@beef/ui-kit';
import { DropdownToggle } from './Dropdown/DropdownToggle';
import { DropdownList } from './Dropdown/DropdownList';
import { DatesPicker } from './DatesPicker';

// Styles
import styles from './styles.pcss';

const cx = classNames.bind(styles);

const isCurrentDay = (date) => {
  const currentDate = new Date();
  return currentDate.getDate() === date?.getDate() && currentDate.getMonth() === date?.getMonth();
};

/**
 * @param title - заголовок календаря
 * @param isModal - определят стили/поведение календаря в модальном окне
 * @param endPeriod - конец периода
 * @param startPeriod - начало периода
 * @param setPeriod - изменение периода
 * @param setEndPeriod - - action изменения конца периода
 * @param setStartPeriod - action изменения начала периода
 * @param isValidPeriod - определяет вадидность введенного периода
 * @param dropdown - элементы списка
 * @param errorMessage - сообщение об ошибке
 */

const PeriodSelection = ({
  title,
  isModal,
  setPeriod,
  endPeriod,
  startPeriod,
  setEndPeriod,
  setStartPeriod,
  isValidPeriod,
  errorMessage,
  dropdown,
}) => {
  const [isDayToggle, setIsDayToggle] = useState(
    !isModal && isCurrentDay(startPeriod),
  );
  const [selectedDropdown, setSelectedDropdown] = useState(dropdown[0].slug);
  const [activeOption, setActiveOption] = useState(getCurrentDay());
  const [isOpen, setOpen] = useState(false);
  const ref = useRef(null);

  useOnClickOutside({
    ref, handler: () => setOpen(false),
  });
  const toggle = useCallback(() => setOpen(!isOpen), [isOpen]);

  const handleChangeDropdown = useCallback((value) => {
    setSelectedDropdown(value);
  }, [selectedDropdown]);

  const onOptionClicked = useCallback((option) => {
    pushDetalizationAnalytics(ANALYTICS_DETAIL.PERIOD_CHANGE,
      {
        period: activeOption.slug,
      });

    if (option.slug !== 'choose_period') {
      pushDetalizationAnalytics(ANALYTICS_DETAIL.PERIOD_CHOOSE,
        {
          period: activeOption.slug ?? 'day',
          period_new: option.slug,
        });
    }

    if (option.slug === 'day' && !isModal) {
      setActiveOption(getCurrentDay());
      setPeriod(option.slug);
      setIsDayToggle(true);
      toggle();
    } else {
      handleChangeDropdown(option.slug);
      setPeriod(option.slug);
      setActiveOption(option);
      setIsDayToggle(false);
      toggle();
    }
  }, [toggle, handleChangeDropdown]);

  const isCalendar = isModal || !isDayToggle;

  return (
    <div
      className={cx('container',
        {
          'container--calendar': isCalendar,
          'container--modal': isModal,
        })}
    >
      {title && (
        <div className={cx('title', { 'title--space': !isCalendar })}>
          <Text tagType="h2" size={isModal ? 'size5-b' : 'size3-m'}>{title}</Text>
        </div>
      )}
      {isCalendar && (
        <div className={cx('dates', { 'dates--error': !isValidPeriod })}>
          <DatesPicker
            setEndPeriod={setEndPeriod}
            setStartPeriod={setStartPeriod}
            activeOption={activeOption}
            endPeriod={endPeriod}
            startPeriod={startPeriod}
            isModal={isModal}
          />
          {!isValidPeriod && (
            <div className={cx('error-message')}>
              <Text color="scarlet" size="size6-r">{errorMessage}</Text>
            </div>
          )}
        </div>
      )}

      <div ref={ref} className={cx('dropdown')}>
        <DropdownToggle
          isOpen={isOpen}
          isModal={isModal}
          onClick={toggle}
          option={activeOption}
          isDayToggle={isDayToggle}
        />
        {isOpen && (
          <DropdownList
            activeOption={activeOption}
            onOptionClicked={onOptionClicked}
            options={dropdown}
            isDayToggle={isDayToggle}
          />
        )}
      </div>
    </div>
  );
};

PeriodSelection.propTypes = {
  title: PropTypes.string,
  setStartPeriod: PropTypes.func,
  setEndPeriod: PropTypes.func,
  setPeriod: PropTypes.func,
  isModal: PropTypes.bool,
  startPeriod: PropTypes.string,
  endPeriod: PropTypes.string,
  isValidPeriod: PropTypes.bool,
  dropdown: PropTypes.array,
  errorMessage: PropTypes.string,
};

export { PeriodSelection };
